import { useState } from "react";
import { useSelector } from "react-redux";
import Draggable from "react-draggable";
import classNames from "classnames";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { Loader } from "@remo-co/ui-core/src/components/Loader";
import { Tooltip } from "@remo-co/ui-core/src/components/Tooltip";
import { OpenInNew } from "@remo-co/ui-core/src/icons/OpenInNew";
import { Close } from "@remo-co/ui-core/src/icons/Close";
import { ArrowForward } from "@remo-co/ui-core/src/icons/ArrowForward";
import { ContentData } from "graphql/generated";
import { useAppDispatch } from "store/hooks";
import { useI18n } from "i18n";
import { selectIsMobile } from "modules/deviceInfo";
import { dismissConversationModeContent } from "../../redux/slice";
import { selectHasContentQueueConversationMode } from "../../redux/selectors";
import useStyles from "./styles";

interface Props {
  content: ContentData;
}

const EmbedContentDisplay = ({ content }: Props) => {
  const dispatch = useAppDispatch();
  const isMobileView = useSelector(selectIsMobile);
  const hasContentQueue = useSelector(selectHasContentQueueConversationMode);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useI18n(["event", "common"]);
  const styles = useStyles();

  const handleDismissContent = () => {
    dispatch(dismissConversationModeContent(content.id));
    setIsLoading(false);
  };

  const handleContentLoad = () => {
    setIsLoading(false);
  };

  return (
    <Draggable handle={`.${styles.header}`} disabled={isMobileView}>
      <Container
        className={classNames(styles.content, {
          [styles.contentMobile]: isMobileView,
        })}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          className={classNames(styles.header, {
            [styles.headerMobile]: isMobileView,
          })}
        >
          <Button<"a">
            variant="text"
            color="dark"
            size="sm"
            contentClassName={styles.openInNewTabButton}
            href={content.url}
            target="_blank"
            rel="noreferrer"
            data-testid="open-new-tab-button"
          >
            {t("open.in.new.tab")}
            <OpenInNew className={styles.openInNewTabButtonIcon} />
          </Button>
          <Tooltip
            title={
              hasContentQueue
                ? t("common:button.next")
                : t("common:button.close")
            }
            placement="top"
          >
            <Button
              variant="ghost"
              color="dark"
              size="sm"
              onClick={handleDismissContent}
              isIconButton
              data-testid="dismiss-button"
            >
              {hasContentQueue ? (
                <ArrowForward data-testid="arrow-forward-icon" />
              ) : (
                <Close data-testid="close-icon" />
              )}
            </Button>
          </Tooltip>
        </Box>
        <Box
          display="flex"
          className={classNames(styles.loadingContainer, {
            [styles.loadingContainerMobile]: isMobileView,
          })}
        >
          {isLoading && (
            <div className={styles.loading}>
              <Loader data-testid="loading-indicator" />
            </div>
          )}
          <iframe
            src={content.url}
            frameBorder={0}
            className={styles.iframe}
            onLoad={handleContentLoad}
            title="URL preview"
          />
        </Box>
      </Container>
    </Draggable>
  );
};

export default EmbedContentDisplay;
